import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import {
  Home,
  ShoppingBag,
  Dumbbell,
  Target,
  Users,
  BookOpen,
  MessageCircle,
  Settings,
  Shield,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  Waves
} from 'lucide-react';

export default function Layout() {
  const user = useAuthStore(state => state.user);
  const profile = useAuthStore(state => state.profile);
  const logout = useAuthStore(state => state.logout);
  const navigate = useNavigate();
  
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Navigation items
  const navItems = [
    { path: '/dashboard', icon: Home, label: 'Dashboard' },
    { path: '/store', icon: ShoppingBag, label: 'Store' },
    { path: '/workouts', icon: Dumbbell, label: 'Workouts' },
    { path: '/goals', icon: Target, label: 'Goals' },
    { path: '/community', icon: Users, label: 'Community' },
    { path: '/glossary', icon: BookOpen, label: 'Glossary' },
  ];

  // Conditional nav items
  const conditionalNavItems = [];
  
  // Chat - Elite Level 2+ only
  if (profile?.tier === 'elite' && profile?.subscription_level >= 2) {
    conditionalNavItems.push({ path: '/chat', icon: MessageCircle, label: 'Coach Chat' });
  }

  // Settings always available
  conditionalNavItems.push({ path: '/settings', icon: Settings, label: 'Settings' });

  // Admin Suite
  if (user?.isAdmin) {
    conditionalNavItems.push({ path: '/admin-suite', icon: Shield, label: 'Admin Suite' });
  }

  const allNavItems = [...navItems, ...conditionalNavItems];

  // Tier badge styling
  const getTierBadgeClass = () => {
    const tier = profile?.tier?.toLowerCase() || 'beginner';
    const badges = {
      elite: 'badge-elite',
      advanced: 'badge-advanced',
      intermediate: 'badge-intermediate',
      beginner: 'badge-beginner'
    };
    return badges[tier] || badges.beginner;
  };

  const NavItem = ({ item, collapsed, onClick }) => (
    <NavLink
      to={item.path}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group
        ${isActive 
          ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-swim' 
          : 'text-secondary-300 hover:bg-white/10 hover:text-white'
        }
        ${collapsed ? 'justify-center' : ''}`
      }
    >
      <item.icon size={20} className="flex-shrink-0" />
      {!collapsed && <span className="font-medium">{item.label}</span>}
    </NavLink>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Desktop Sidebar */}
      <aside 
        className={`
          hidden md:flex flex-col bg-gradient-swimcraft text-white
          transition-all duration-300 ease-in-out fixed h-full z-30
          ${sidebarCollapsed ? 'w-20' : 'w-64'}
        `}
      >
        {/* Logo & Brand */}
        <div className={`p-4 border-b border-white/10 ${sidebarCollapsed ? 'px-3' : ''}`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Waves className="text-primary-300" size={24} />
            </div>
            {!sidebarCollapsed && (
              <div>
                <h1 className="font-bold text-lg leading-tight">SwimCraft</h1>
                <p className="text-xs text-primary-200">Studio</p>
              </div>
            )}
          </div>
        </div>

        {/* User Profile Section */}
        <div className={`p-4 border-b border-white/10 ${sidebarCollapsed ? 'px-2' : ''}`}>
          <div className={`flex items-center gap-3 ${sidebarCollapsed ? 'flex-col' : ''}`}>
            <div className="w-10 h-10 bg-primary-400/30 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-lg font-bold text-primary-200">
                {profile?.first_name?.[0] || user?.email?.[0]?.toUpperCase() || '?'}
              </span>
            </div>
            {!sidebarCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">
                  {profile?.first_name || 'Swimmer'}
                </p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className={`${getTierBadgeClass()}`}>
                    {profile?.tier || 'Beginner'}
                    {profile?.tier === 'elite' && profile?.subscription_level && ` L${profile.subscription_level}`}
                  </span>
                  {user?.isAdmin && (
                    <span className="badge bg-red-500 text-white">
                      Admin
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto scrollbar-thin">
          {allNavItems.map(item => (
            <NavItem key={item.path} item={item} collapsed={sidebarCollapsed} />
          ))}
        </nav>

        {/* Collapse Toggle & Logout */}
        <div className="p-4 border-t border-white/10 space-y-2">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl
                       text-secondary-300 hover:bg-white/10 hover:text-white transition-colors"
          >
            {sidebarCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
            {!sidebarCollapsed && <span className="text-sm">Collapse</span>}
          </button>
          
          <button
            onClick={handleLogout}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl
                       text-red-300 hover:bg-red-500/20 hover:text-red-200 transition-colors
                       ${sidebarCollapsed ? 'justify-center' : ''}`}
          >
            <LogOut size={20} />
            {!sidebarCollapsed && <span className="font-medium">Log Out</span>}
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-gradient-swimcraft text-white px-4 py-3 flex items-center justify-between shadow-lg safe-top">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
            <Waves className="text-primary-300" size={20} />
          </div>
          <span className="font-bold">SwimCraft Studio</span>
        </div>
        <button
          onClick={() => setMobileMenuOpen(true)}
          className="p-2 rounded-lg hover:bg-white/10 transition-colors"
        >
          <Menu size={24} />
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 animate-fade-in" 
            onClick={() => setMobileMenuOpen(false)}
          />
          
          {/* Menu Panel */}
          <div className="absolute top-0 right-0 bottom-0 w-72 bg-gradient-swimcraft text-white flex flex-col animate-slide-in safe-top">
            {/* Header */}
            <div className="p-4 flex items-center justify-between border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                  <Waves className="text-primary-300" size={20} />
                </div>
                <span className="font-bold">Menu</span>
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* User Info */}
            <div className="p-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-primary-400/30 rounded-full flex items-center justify-center">
                  <span className="text-xl font-bold text-primary-200">
                    {profile?.first_name?.[0] || user?.email?.[0]?.toUpperCase() || '?'}
                  </span>
                </div>
                <div>
                  <p className="font-medium">{profile?.first_name || 'Swimmer'}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`${getTierBadgeClass()}`}>
                      {profile?.tier || 'Beginner'}
                    </span>
                    {user?.isAdmin && (
                      <span className="badge bg-red-500 text-white">Admin</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
              {allNavItems.map(item => (
                <NavItem 
                  key={item.path} 
                  item={item} 
                  collapsed={false}
                  onClick={() => setMobileMenuOpen(false)}
                />
              ))}
            </nav>

            {/* Logout */}
            <div className="p-4 border-t border-white/10 safe-bottom">
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleLogout();
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl
                           text-red-300 hover:bg-red-500/20 hover:text-red-200 transition-colors"
              >
                <LogOut size={20} />
                <span className="font-medium">Log Out</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <main className={`flex-1 min-h-screen transition-all duration-300 ${sidebarCollapsed ? 'md:ml-20' : 'md:ml-64'}`}>
        <div className="pt-16 md:pt-0 min-h-full">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
